import { size } from "lodash-es";

import type { ConnectedNode } from "../../../../graph/types.ts";
import {
  isConnectedIdentityTypeNode,
  isConnectedNode,
} from "../../../../graph/types.ts";
import { arnInfo } from "../../../../integrations/resources/aws/util.ts";
import type {
  AssessmentNodes,
  GrantNode,
  IdentityType,
} from "../../../../types/assessment/data/index.ts";
import type { FixOptions } from "../../../../types/assessment/monitor.ts";
import { Monitor } from "../../../../types/assessment/monitor.ts";

const commandsForIdentityType = (identityType: IdentityType) => {
  switch (identityType) {
    case "user":
      return {
        attachCmd: "attach-user-policy",
        detachCmd: "detach-user-policy",
        nameParam: "--user-name",
      };
    case "group":
      return {
        attachCmd: "attach-group-policy",
        detachCmd: "detach-group-policy",
        nameParam: "--group-name",
      };
    case "aws-iam-role":
      return {
        attachCmd: "attach-role-policy",
        detachCmd: "detach-role-policy",
        nameParam: "--role-name",
      };
    default:
      return {
        attachCmd: `# Unsupported`,
        detachCmd: `# Unsupported`,
        nameParam: "",
      };
  }
};

const unusedGrantFixGenerator = {
  aws: {
    description: "To fix, run these commands in AWS CloudShell:",
    type: "shell" as const,
    generate: ({ data }: GrantNode) => {
      const { principal, principalType, privilegeSet } = data;

      const { attachCmd, detachCmd, nameParam } =
        commandsForIdentityType(principalType);

      if (attachCmd.startsWith("#")) {
        return `# Unsupported principal type: ${principalType}`;
      }

      return `aws iam ${detachCmd} \\
${nameParam} ${principal} \\
--policy-arn ${privilegeSet}`;
    },
  },
};

const unusedPermissionSetFixGenerator = {
  aws: {
    description: `Remove all assignments for this permission set by running these commands in AWS Cloud Shell. 
    This must be run in the account that contains the AWS Identity Center instance this permission set is bound to.`,
    type: "shell" as const,
    generate: ({ data, parents }: GrantNode) => {
      const roleAssignmentParent = parents.find(
        isConnectedIdentityTypeNode("aws-permission-set-role")
      );

      const permissionSetParent:
        | ConnectedNode<AssessmentNodes, "permissionSet">
        | undefined = roleAssignmentParent?.parents.find(
        isConnectedNode("permissionSet")
      );

      const permissionSetArn = permissionSetParent?.key;
      const accountId = data?.parent;

      if (!permissionSetArn || !accountId) {
        return `# Not able to find permission set.  Please contact support if this issue persists.`;
      }

      return `
# List all account assignments for this permission set and account
INSTANCES=$(aws sso-admin list-instances \\
  --query 'Instances[*]' \\
  --output json)

if [ $(echo $INSTANCES | jq -r 'length') -eq 0 ]; then
  echo "No instances found.  This command should be run in the AWS account and region that contains an AWS Identity Center instance this permission set is bound to."
fi

# For each instance, call describe-permission-set and get the permission set arn
echo $INSTANCES | jq -c '.[]' | while read -r instance; do
  INSTANCE_ARN=$(echo $instance | jq -r '.InstanceArn')
  echo "Listing permission sets for instance $INSTANCE_ARN..."
    PERMISSION_SET_ARN=$(aws sso-admin describe-permission-set \\
    --instance-arn $INSTANCE_ARN \\
    --permission-set-arn ${permissionSetArn} \\
    --query 'PermissionSet.PermissionSetArn' \\
    --output json)

  if [ -z "$PERMISSION_SET_ARN" ]; then
    echo "No permission set found for instance $INSTANCE_ARN"
    continue
  fi

  echo "Found binding for permission set $PERMISSION_SET_ARN and instance $INSTANCE_ARN"

  ASSIGNMENTS_JSON="[]"
  NEXT_TOKEN=""
  
  while true; do
    TOKEN_PARAM=""
    if [ -n "$NEXT_TOKEN" ]; then
      TOKEN_PARAM="--starting-token $NEXT_TOKEN"
    fi
    
    PAGE_RESULT=$(aws sso-admin list-account-assignments \\
    --instance-arn $INSTANCE_ARN \\
    --account-id ${accountId} \\
    --permission-set-arn ${permissionSetArn} \\
    $TOKEN_PARAM)
    
    PAGE_ASSIGNMENTS=$(echo "$PAGE_RESULT" | jq -r '.AccountAssignments')
    NEXT_TOKEN=$(echo "$PAGE_RESULT" | jq -r '.NextToken // empty')
    
    # Combine with existing results
    ASSIGNMENTS_JSON=$(echo "$ASSIGNMENTS_JSON" | jq --argjson new "$PAGE_ASSIGNMENTS" -r '. + $new')
    
    # Break if no more pages
    if [ -z "$NEXT_TOKEN" ]; then
      break
    fi
  done
  
  ASSIGNMENTS="$ASSIGNMENTS_JSON"
  
  # Exit if no assignments found
  if [ "$(echo $ASSIGNMENTS | jq 'length')" = "0" ]; then
    echo "No assignments found for permission set ${permissionSetArn} in account ${accountId}"
  fi
  echo "Found assignments $ASSIGNMENTS"
  echo $ASSIGNMENTS | jq -c '.[]' | while read -r assignment; do
    PRINCIPAL_ID=$(echo $assignment | jq -r '.PrincipalId')
    PRINCIPAL_TYPE=$(echo $assignment | jq -r '.PrincipalType')
    
    echo "Deleting assignment for $PRINCIPAL_TYPE $PRINCIPAL_ID..."

    aws sso-admin delete-account-assignment \\
      --instance-arn $INSTANCE_ARN \\
      --permission-set-arn ${permissionSetArn} \\
      --principal-id $PRINCIPAL_ID \\
      --principal-type $PRINCIPAL_TYPE \\
      --target-id ${accountId} \\
      --target-type AWS_ACCOUNT
  done
done`;
    },
  },
};

const partialGrantFixGenerator = {
  aws: {
    description:
      "Remove excessive privileges by creating a custom policy with only used permissions and detaching the old policy.",
    type: "shell" as const,

    generate: ({ data, aggregates, key }: GrantNode, { scope }: FixOptions) => {
      if (!scope.id) {
        return `# No project found for node ${key}. Unable to generate partial fix.`;
      }

      const {
        privilegeSet: policyArn,
        principalType,
        resources,
        principal,
      } = data;

      try {
        const info = arnInfo(principal);
        const principalLabel = info.name;

        if (!principalLabel || !policyArn) {
          return `# No principal or policy found for node ${key}. Unable to generate partial fix.`;
        }

        const { attachCmd, detachCmd, nameParam } =
          commandsForIdentityType(principalType);
        if (attachCmd.startsWith("#")) {
          return `# Unsupported principal type: ${principalType}`;
        }

        const { used = [], unknown = [] } = aggregates?.privileges ?? {};
        const usedAndUnknownActions = [...used, ...unknown].map((p) => p.key);

        // If there are no used/unknown actions, just detach the old policy.
        if (!usedAndUnknownActions.length) {
          return `# This command set removes the old policy ("${policyArn}")
aws iam ${detachCmd} \\
${nameParam} ${principalLabel} \\
--policy-arn ${policyArn}
    `;
        }

        if (size(resources) === 0) {
          return `# No resource found for grant ${key}. Unable to generate partial fix.`;
        }
        if (size(resources) > 1) {
          return `# Multiple resources found for grant ${key}. Unable to generate partial fix.`;
        }

        const [resource] = resources;

        const shortOldPolicy = policyArn.split("/").at(-1);
        if (!shortOldPolicy) {
          return `# No permission set found for node ${key}. Unable to generate partial fix.`;
        }

        const formattedOldPolicy = shortOldPolicy.slice(0, 36);
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const baseNamePrefix =
          `Lp4$${formattedOldPolicy}${randomSuffix}`.replace(
            /[^A-Za-z0-9+=,.@_-]/g,
            ""
          );

        // AWS policies have a size limit of approximately 6144 characters
        // We'll split the actions into multiple policies if needed
        const MAX_ACTIONS_PER_POLICY = 100;
        const actionBatches = [];

        for (
          let i = 0;
          i < usedAndUnknownActions.length;
          i += MAX_ACTIONS_PER_POLICY
        ) {
          actionBatches.push(
            usedAndUnknownActions.slice(i, i + MAX_ACTIONS_PER_POLICY)
          );
        }

        let commands = `# This command set removes the old policy ("${policyArn}") 
# and creates new least-privileged policies with just the used/unknown actions.
# ${
          actionBatches.length > 1
            ? `Since there are many actions, they have been split into ${actionBatches.length} policies.`
            : ""
        }

`;

        const awsPartition = arnInfo(policyArn).partition;
        const policyArns = [];

        actionBatches.forEach((batch, index) => {
          const policyName = `${baseNamePrefix}${
            actionBatches.length > 1 ? `-part${index + 1}` : ""
          }`;
          const policyFileName = `/tmp/${policyName}.json`;
          const policyArn = `arn:${awsPartition}:iam::${scope.id}:policy/${policyName}`;
          policyArns.push(policyArn);

          const policyDoc = JSON.stringify(
            {
              Version: "2012-10-17",
              Statement: [
                {
                  Effect: "Allow",
                  Action: batch,
                  Resource: resource,
                },
              ],
            },
            null,
            2
          );

          commands += `cat > ${policyFileName} <<EOF
${policyDoc}
EOF

# Create a new policy with only used privileges
aws iam create-policy \\
--policy-name ${policyName} \\
--policy-document file://${policyFileName}

# Attach the new policy
aws iam ${attachCmd} \\
${nameParam} ${principalLabel} \\
--policy-arn ${policyArn}

`;
        });

        // Only detach the old policy after all new policies are attached
        commands += `# Finally, detach the old policy
aws iam ${detachCmd} \\
${nameParam} ${principalLabel} \\
--policy-arn ${policyArn}
`;

        return commands;
      } catch {
        return `# No principal or policy found for node ${key}. Unable to generate partial fix.`;
      }
    },
  },
};

export const AwsGrantPresets = {
  "Unused AWS Policy Assignments": Monitor({
    show: "grant",
    scopes: ["aws"],
    searchTerm:
      'usage:type:"unused" ^usage:type:!"unused" ^grant=isProviderManaged:"true" identity=status:"active"',
    label: "Unused AWS policy assignment",
    priority: "HIGH",
    cta: "No privileges given by this policy assignment have been used in the last 90 days. Consider removing this policy assignment.",
    remediation: (action: string) =>
      `No privileges given by this policy assignment have been used in the last 90 days. P0 has automatically ${action} to remove this policy assignment.`,
    description:
      "Policy assignments for which no actions have been used in the last 90 days.",
    fix: unusedGrantFixGenerator,
  }),
  "Unused AWS Permission Set Assignments": Monitor({
    show: "grant",
    scopes: ["aws"],
    searchTerm: 'permissionSet: ^usage:type:!"unused"',
    label: "Unused AWS permission set",
    priority: "HIGH",
    cta: "Actions granted by this permission-set assignment have not been used in the last 90 days. Consider removing this assignment.",
    description:
      "Permission sets for which no actions have been used in the last 90 days.",
    fix: unusedPermissionSetFixGenerator,
  }),
  "Excessive AWS Policies": Monitor({
    scopes: ["aws"],
    show: "grant",
    // nodes that can reach an unused permission AND can reach a used or unknown permission
    searchTerm:
      'usage:type:"unused" usage:type:!"unused" ^grant=isProviderManaged:"true"',
    label: "Excessive AWS policies",
    priority: "MEDIUM",
    cta: "This grant has more privileges than are required for its intended use. Consider replacing this grant with a least-privileged role.",
    description:
      "Grants with more privileges than are required for their intended use.",
    fix: partialGrantFixGenerator,
  }),
  "Privileged AWS Policies": Monitor({
    scopes: ["aws"],
    show: "grant",
    searchTerm: 'risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged AWS policies",
    priority: "MEDIUM",
    cta: "This policy allows access with critical risks. Consider replacing this policy with a low-sensitivity policy, and using just-in-time access for ephemeral access.",
    description: "Policies with any privileges that allow sensitive access.",
  }),
  "AWS Policies with Unused Privileged Access": Monitor({
    scopes: ["aws"],
    show: "grant",
    searchTerm:
      'usage:type:"unused"->risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "AWS policies with unused privileged access",
    priority: "CRITICAL",
    cta: "This policy conveys privileges that allow critical risks, and these privileges have not been used in the last 90 days. Consider replacing this policy with a policy that does not include these privileges.",
    description: "Policies with unused privileges that allow sensitive access.",
    fix: partialGrantFixGenerator,
  }),
};
